import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Play, Loader2, Save, RefreshCw, X, Trash2, Gift, Database
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const OfferingPanel = ({ onRefresh }) => {
  const [formData, setFormData] = useState({
    title: 'Offering & Thanksgiving',
    message: 'Give, and it shall be given unto you; good measure, pressed down, and shaken together, and running over.',
    scripture: 'Luke 6:38',
    bank_details: '',
    giving_url: '',
    instructions: ''
  });
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/display/offering-templates`);
      setTemplates(res.data || []);
      const defaultTemplate = (res.data || []).find(t => t.is_default);
      if (defaultTemplate) {
        setFormData({
          title: defaultTemplate.title || 'Offering & Thanksgiving',
          message: defaultTemplate.message || '',
          scripture: defaultTemplate.scripture || '',
          bank_details: defaultTemplate.bank_details || '',
          giving_url: defaultTemplate.giving_url || '',
          instructions: defaultTemplate.instructions || ''
        });
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleShow = async () => {
    try {
      await axios.post(`${API}/display/show/offering`, formData);
      alert('Offering display activated!');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/display/offering-templates`, { ...formData, is_default: true });
      alert('Offering template saved!');
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
    }
    setSaving(false);
  };

  const handleLoadTemplate = (template) => {
    setFormData({
      title: template.title || '', message: template.message || '',
      scripture: template.scripture || '', bank_details: template.bank_details || '',
      giving_url: template.giving_url || '', instructions: template.instructions || ''
    });
    setShowTemplates(false);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/display/offering-templates/${templateId}`);
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const fields = [
    { label: 'Title', key: 'title', type: 'input', testId: 'offering-title-input' },
    { label: 'Scripture Message', key: 'message', type: 'textarea', rows: 3, testId: 'offering-message-input' },
    { label: 'Scripture Reference', key: 'scripture', type: 'input', testId: 'offering-scripture-input' },
    { label: 'Bank Details (shown on display)', key: 'bank_details', type: 'textarea', rows: 2, placeholder: 'e.g. Sort Code: 00-00-00 | Account: 12345678', testId: 'offering-bank-details-input' },
    { label: 'Online Giving URL (optional)', key: 'giving_url', type: 'input', placeholder: 'https://give.church/your-church', testId: 'offering-giving-url-input' },
    { label: 'Additional Instructions', key: 'instructions', type: 'input', placeholder: 'e.g. Please use the offering bags being passed around', testId: 'offering-instructions-input' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 data-testid="offering-title" className="text-lg font-semibold text-slate-800">Offering Display</h3>
        <div className="flex gap-2">
          {templates.length > 0 && (
            <button data-testid="load-template-btn" onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
              <Database className="w-4 h-4" /> Templates ({templates.length})
            </button>
          )}
          <button data-testid="save-template-btn" onClick={handleSaveTemplate} disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save as Default
          </button>
        </div>
      </div>

      {showTemplates && templates.length > 0 && (
        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
          <p className="text-xs font-medium text-slate-500 mb-1">Saved Templates</p>
          {templates.map(t => (
            <div key={t.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100">
              <button onClick={() => handleLoadTemplate(t)} className="flex-1 text-left text-sm text-slate-700 hover:text-blue-600">
                <span className="font-medium">{t.title}</span>
                {t.is_default && <span className="ml-2 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Default</span>}
              </button>
              <button onClick={() => handleDeleteTemplate(t.id)} className="p-1 text-red-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea data-testid={f.testId} value={formData[f.key]}
                  onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                  rows={f.rows} placeholder={f.placeholder}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl resize-none text-sm" />
              ) : (
                <input type="text" data-testid={f.testId} value={formData[f.key]}
                  onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm" />
              )}
            </div>
          ))}
          <button data-testid="show-offering-btn" onClick={handleShow}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700">
            <Play className="w-5 h-5" /> Display Offering Screen
          </button>
        </div>

        <div className="p-8 bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 rounded-xl text-white text-center flex flex-col justify-center">
          <Gift className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h4 className="text-2xl font-bold mb-4">{formData.title}</h4>
          <p className="text-lg text-white/90 italic mb-4">"{formData.message}"</p>
          <p className="text-amber-400 font-medium mb-4">{formData.scripture}</p>
          {formData.bank_details && (
            <div className="mt-2 p-3 bg-white/10 rounded-lg">
              <p className="text-xs text-amber-300 uppercase tracking-wider mb-1">Bank Details</p>
              <p className="text-sm text-white/90 whitespace-pre-line">{formData.bank_details}</p>
            </div>
          )}
          {formData.giving_url && <p className="text-xs text-amber-300 mt-3">Online: {formData.giving_url}</p>}
          {formData.instructions && <p className="text-sm text-white/70 mt-3">{formData.instructions}</p>}
        </div>
      </div>
    </div>
  );
};

export default OfferingPanel;
